import React, { useRef, useEffect } from 'react';
import DOMPurify from 'dompurify';
import './RichText.css';

interface Props {
  value: string;
  onChange: (v: string) => void;
  onBlur?: (v: string) => void;
  readOnly?: boolean;
  placeholder?: string;
  minHeight?: number;
}

const BTN: React.CSSProperties = {
  background: 'none',
  border: '1px solid rgba(255,255,255,0.15)',
  color: 'var(--white)',
  borderRadius: 4,
  padding: '2px 7px',
  cursor: 'pointer',
  fontSize: 13,
  lineHeight: '20px',
};

const RichText: React.FC<Props> = ({
  value,
  onChange,
  onBlur,
  readOnly,
  placeholder = 'Escriba aquí…',
  minHeight = 100,
}) => {
  const ref = useRef<HTMLDivElement>(null);
  const lastHtml = useRef('');

  // Inicializar contenido al montar
  useEffect(() => {
    if (ref.current) {
      ref.current.innerHTML = value || '';
      lastHtml.current = value || '';
    }
  }, []); // eslint-disable-line

  // Sincronizar cuando el value cambia externamente (ej: resultado IA)
  // Compara con el DOM real, no con lastHtml.current, para detectar cambios externos correctamente
  useEffect(() => {
    if (!ref.current) return;
    if (ref.current.innerHTML !== (value || '')) {
      ref.current.innerHTML = value || '';
      lastHtml.current = value || '';
    }
  }, [value]);

  const emit = () => {
    const html = ref.current?.innerHTML || '';
    if (html === lastHtml.current) return;
    lastHtml.current = html;
    onChange(html);
  };

  const handleBlur = () => {
    emit();
    onBlur?.(lastHtml.current);
  };

  const exec = (e: React.MouseEvent, cmd: string) => {
    e.preventDefault(); // evita que el editor pierda el foco
    document.execCommand(cmd, false, undefined);
    ref.current?.focus();
    emit();
  };

  if (readOnly) {
    const safeHtml = value
      ? DOMPurify.sanitize(value)
      : `<span style="color:rgba(255,255,255,0.3)">${placeholder}</span>`;
    return <div className="rte-view" dangerouslySetInnerHTML={{ __html: safeHtml }} />;
  }

  return (
    <div className="p-input" style={{ padding: 0, overflow: 'hidden' }}>
      <div className="rte-toolbar">
        <button type="button" style={BTN} title="Negrita (Ctrl+B)" onMouseDown={e => exec(e, 'bold')}>
          <b>B</b>
        </button>
        <button type="button" style={{ ...BTN, fontStyle: 'italic' }} title="Cursiva (Ctrl+I)" onMouseDown={e => exec(e, 'italic')}>
          <i>I</i>
        </button>
        <button type="button" style={BTN} title="Subrayado (Ctrl+U)" onMouseDown={e => exec(e, 'underline')}>
          <u>U</u>
        </button>
        <span className="rte-sep" />
        <button type="button" style={BTN} title="Lista numerada" onMouseDown={e => exec(e, 'insertOrderedList')}>
          1.
        </button>
        <button type="button" style={BTN} title="Lista con viñetas" onMouseDown={e => exec(e, 'insertUnorderedList')}>
          •
        </button>
        <span className="rte-sep" />
        <button type="button" style={{ ...BTN, fontSize: 11 }} title="Quitar formato" onMouseDown={e => exec(e, 'removeFormat')}>
          ✕ fmt
        </button>
      </div>
      <div
        ref={ref}
        contentEditable
        suppressContentEditableWarning
        data-placeholder={placeholder}
        onInput={emit}
        onBlur={handleBlur}
        style={{
          minHeight,
          padding: '10px 12px',
          outline: 'none',
          lineHeight: 1.65,
          fontSize: 14,
          color: 'var(--white)',
        }}
      />
    </div>
  );
};

export default RichText;
